/* eslint-disable max-len */
import { ArrowLeftOutlined, ArrowRightOutlined, UndoOutlined } from '@ant-design/icons';
import {
  Button,
  PageHeader,
  Tabs,
  Tooltip
} from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import allActions from '../../actions';
import * as loadingKeys from '../../actions/MaterialDetailsDomain/LoadingKeys';
import { getLongText, getMaterial } from '../../actions/MaterialDetailsDomain/LoadingKeys';
import Loading from '../../components/loading/Loading';
import hasAuthenticatedRole from '../../utils/AuthFunctions';
import { formatMaterialNumber } from '../../utils/DateAndNumberFunctions';
import { getSpecialRulePdTargetPlants } from '../../utils/mrpSpecialRules';
import { modes } from '../MaterialListView/utils/MaterialListFunctions';
import CalendarModal from './components/calendarModal/CalendarModal';
import HeaderOptions from './components/headerOptions/HeaderOptions';
import './MaterialDetails.css';
import PlantElements from './plantElements/PlantElements';
import styles from './styles.module.scss';
import { createIOTAPayload } from './summaryElements/SummaryComponents/Recommendations/Generators';
import SummaryElements from './summaryElements/SummaryElements';
import TabElements from './tabElements/TabElements';

const MaterialDetails = () => {
  const { TabPane } = Tabs;
  const screens = useBreakpoint();
  const { plant, material } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const worklistData = useSelector((state) => state.worklistState.staticWorklist);
  const classification = useSelector((state) => state.materialDetailsState?.classification);
  const relatedPlants = useSelector((state) => state.materialDetailsState.plantRelationships);
  const plants = useSelector((state) => state.commonState.plants);
  const configTable = useSelector((state) => state.commonState.configTable);
  const data = useSelector((state) => state.materialDetailsState.material);
  const loading = useSelector((state) => state.materialDetailsState.loading);
  const user = useSelector((state) => state.authState.user);
  const actionComplete = useSelector((state) => state.materialDetailsState.actionComplete);
  const mrpData = useSelector((state) => state.materialDetailsState.mrpSettings);
  const selectedRowKeys = useSelector((state) => state.materialState?.selectedRowKeys || []);
  const matList = useSelector((state) => state.materialState?.materialListData);
  const dataSrc = useSelector((state) => state.commonState?.dataSrc);
  const [simulationVisible, setSimulationVisible] = useState(false);
  const [calenderVisible, setCalenderVisible] = useState(false);
  const [fetchingMaterial, setFetchingMaterial] = useState(false);
  const requestedMrpFetchesRef = useRef(new Set());
  const requestedAdditionalMaterialFetchesRef = useRef(new Set());
  const [dataIsUpdated, setDataIsUpdated] = useState(false);
  const [navList, setNavList] = useState(null);
  const [excludePM10, setExcludePM10] = useState();
  const [canEdit, setCanEdit] = useState(false);
  const [currentCalender, setCalenderObj] = useState({
    text: null,
    visible: null,
    onOK: null,
    onCancel: null,
  });
  const [dateObj, setDateObj] = useState({
    review: null,
    inspect: null,
    postpone: null,
  });

  const [notFound, setNotFound] = useState(false);
  const { sortedRowKeys } = location.state || {};
  const effectiveRowKeys = sortedRowKeys || selectedRowKeys;
  const currentPlant = plant ?? '';
  const currentMaterial = material ?? '';

  useEffect(() => {
    if (!user?.accessToken) return;
    if (material[0] === '0') {
      history.push(`/material-details/${plant}/${Number.parseInt(material, 10)}`);
    } else if (!fetchingMaterial && (!data || !(data.some((_material) => (_material.MATERIAL_TYPE_SAP_ID === material
      && _material.PLANT_FACILITY_SAP_ID === plant))))) {
      setDataIsUpdated(false);
      setFetchingMaterial(true);
      dispatch(allActions.MaterialDetailsActions.setLoading(getMaterial));
      dispatch(allActions.MaterialDetailsActions.fetchMaterial(plant, material));
      dispatch(allActions.MaterialDetailsActions.fetchPlantRelationships(plant));
      dispatch(allActions.MaterialDetailsActions.getClassification(material));
    }
    dispatch(allActions.MaterialDetailsActions.fetchChangelogs(plant, material));
    dispatch(allActions.MaterialDetailsActions.setLoading(getLongText));
    dispatch(allActions.MaterialDetailsActions.getLongText(plant, material));
  }, [plant, material, user?.accessToken]);

  useEffect(() => {
    if (user?.idTokenClaims?.roles) {
      const userCanEdit = hasAuthenticatedRole(
        user.idTokenClaims.roles,
        process.env.REACT_APP_VIEW_PRICES,
      );
      setCanEdit(userCanEdit);
    }
  }, [user?.idTokenClaims]);

  useEffect(() => {
    if (plants && (!classification || !(classification?.MATERIAL_TYPE_SAP_ID === material && material))) {
      dispatch(allActions.MaterialDetailsActions.getClassification(material));
    }
  }, [plants, plant, material]);

  useEffect(() => {
    if (data.length > 0 && configTable && dataIsUpdated) {
      const { supplier } = data.find((x) => x.PLANT_FACILITY_SAP_ID === plant);
      const excludeOnPlant = configTable.find((x) => x.PLANT_FACILITY_SAP_ID === plant)?.EXCLUDE_PM10;
      const excludeOnSupplier = configTable.find((x) => x.PLANT_FACILITY_SAP_ID === supplier)?.EXCLUDE_PM10;
      setExcludePM10(excludeOnPlant || excludeOnSupplier);
    }
  }, [data, configTable, dataIsUpdated]);

  useEffect(() => {
    setFetchingMaterial(false);
    setDataIsUpdated((data && (data.some((_material) => (_material.MATERIAL_TYPE_SAP_ID === material
      && _material.PLANT_FACILITY_SAP_ID === plant)))));
  }, [data]);

  useEffect(() => {
    requestedMrpFetchesRef.current.clear();
    requestedAdditionalMaterialFetchesRef.current.clear();
  }, [plant, material]);

  useEffect(() => {
    const relatedPlantsForSelection = (relatedPlants && relatedPlants[plant]) || [];

    relatedPlantsForSelection.forEach((_plant) => {
      if (!_plant) return;
      const id = _plant.Plant_ID;
      const hasMaterialData = data && data.some((_material) => (
        _material
          && _material.PLANT_FACILITY_SAP_ID != null
          && id != null
          && String(_material.PLANT_FACILITY_SAP_ID) === String(id)
          && _material.MATERIAL_TYPE_SAP_ID
          && formatMaterialNumber(_material.MATERIAL_TYPE_SAP_ID) === formatMaterialNumber(material)
      ));
      if (hasMaterialData) return;

      const requestKey = `${id}-${material}`;
      if (requestedAdditionalMaterialFetchesRef.current.has(requestKey)) return;

      requestedAdditionalMaterialFetchesRef.current.add(requestKey);
      dispatch(allActions.MaterialDetailsActions.fetchAdditionalMaterial(id, material));
    });

    const relPlants = relatedPlantsForSelection.map((e) => e.Plant_ID);
    const requiredMrpPlants = [...new Set([
      plant,
      ...relPlants,
      ...getSpecialRulePdTargetPlants(plant),
    ])];

    // Check if current MRP settings exist in redux, and avoid re-request loops for plants
    // that never return MRP rows for this material.
    const fetchPlants = requiredMrpPlants.filter((p) => {
      const hasMrp = mrpData && mrpData.some(
        (m) => m.Plant != null && p != null
          && String(m.Plant) === String(p)
          && formatMaterialNumber(m.MaterialNumber) === material,
      );
      if (hasMrp) return false;

      const requestKey = `${p}-${material}`;
      return !requestedMrpFetchesRef.current.has(requestKey);
    });

    if (fetchPlants.length > 0) {
      fetchPlants.forEach((p) => requestedMrpFetchesRef.current.add(`${p}-${material}`));
      dispatch(allActions.MaterialDetailsActions.setLoading(loadingKeys.mrp));
      dispatch(allActions.SapAPIActions.getMRPs(fetchPlants, [material]));
    }
  }, [relatedPlants, plant, material, data, mrpData]);

  useEffect(() => {
    if (actionComplete?.review || actionComplete?.postpone || actionComplete?.inspect) {
      let key;
      let recKey;
      if (actionComplete.review) {
        key = 'Review';
        recKey = 'ReviewDate';
        dispatch(allActions.MaterialDetailsActions.updateReviewData(plant, material));
      } else if (actionComplete.postpone) {
        key = 'Postpone';
        recKey = 'PostponeToDate';
        dispatch(allActions.MaterialDetailsActions.fetchMaterial(plant, material));
      } else {
        key = 'Inspect';
        recKey = 'InspectToDate';
        dispatch(allActions.MaterialDetailsActions.fetchMaterial(plant, material));
      }
      const materialData = data?.find(
        (entry) => entry?.PLANT_FACILITY_SAP_ID === plant && entry?.MATERIAL_TYPE_SAP_ID === material,
      );
      dispatch(allActions.MaterialDetailsActions.clearActionCompleteStatus(key.toLocaleLowerCase()));
      const payload = createIOTAPayload(plant, material, [{
        current: materialData?.[recKey] ?? '-',
        userDefined: dateObj[key.toLocaleLowerCase()],
        key,
      }], user);
      dispatch(allActions.MaterialDetailsActions.createChangelog(plant, material, payload));
    }
  }, [actionComplete]);

  useEffect(() => {
    if (history.location?.state?.mode) {
      if (history.location?.state?.mode === modes.STATIC && worklistData?.length > 1) {
        setNavList(worklistData);
      } else if (history.location?.state?.mode === modes.DYNAMIC && matList?.length > 1) {
        setNavList(matList);
      }
    } else if (matList?.length > 1) {
      setNavList(matList);
    }
  }, [history.location?.state, worklistData, matList]);

  useEffect(() => () => {
    // cleanup-useEffect to clear tabs data when leaving
    dispatch(allActions.MaterialDetailsActions.clearData());
    // this ensures that when leaving through navigation, we don't
    // apply setPrevPath to incorrect value
    if (history.location?.state !== null) {
      dispatch(allActions.MaterialListActions.setPrevPath());
    }
  }, []);

  useEffect(() => {
    setNotFound(loading && (!loading[loadingKeys?.getMaterial] && !fetchingMaterial));
  }, [loading, fetchingMaterial]);

  const backToMaterialList = () => {
    const state = history.location?.state;
    if (state?.worklistID) {
      const worklistUrl = state?.mode === modes.DYNAMIC ? 'work-list' : 'static-work-list';
      history.push(`/${worklistUrl}/${state?.worklistID}`, { prevPath: 'matDetails' });
    } else {
      history.push('/material-list', { prevPath: 'matDetails' });
    }
  };

  const review = () => {
    setDateObj({ ...dateObj, review: new Date() });
    dispatch(allActions.MaterialDetailsActions.review(plant, material, user?.account?.username));
  };

  const postpone = (date) => {
    if (date) {
      setCalenderVisible(false);
      setDateObj({ ...dateObj, postpone: date });
      dispatch(allActions.MaterialDetailsActions.postpone(
        plant, material, date, user?.account?.username, user?.uniqueId, dataSrc?.matList
      ));
    }
  };

  const inspect = (date) => {
    if (date) {
      setCalenderVisible(false);
      setDateObj({ ...dateObj, inspect: date });
      dispatch(allActions.MaterialDetailsActions.inspect(
        plant, material, date, user?.account?.username, user?.uniqueId, dataSrc?.matList
      ));
    }
  };

  const calenderObj = {
    postpone: {
      text: 'Postpone',
      onOK: (date) => postpone(date),
      onCancel: () => setCalenderVisible(false),
    },
    inspect: {
      text: 'Inspect',
      onOK: (date) => inspect(date),
      onCancel: () => setCalenderVisible(false),
    },
  };

  const setCurrentCalender = (name) => {
    setCalenderVisible(true);
    setCalenderObj(calenderObj[name]);
  };

  const getNextSelectedMaterial = (currentPlant, currentMaterial, direction) => {
    let nextPlant = '';
    let nextMaterial = '';

    const currentIndex = selectedRowKeys.findIndex((key) => key === `${currentPlant}${currentMaterial}`);
    if (currentIndex !== -1) {
      const nextIndex = (currentIndex + direction + selectedRowKeys.length) % selectedRowKeys.length;
      const nextKey = selectedRowKeys[nextIndex];
      nextPlant = nextKey.slice(0, 4);
      nextMaterial = nextKey.slice(4);
    }

    return [nextPlant, nextMaterial];
  };

  const goToNewMaterial = (direction) => {
    const hasMultipleSelected = selectedRowKeys?.length > 1;
    const currentKey = `${currentPlant}${currentMaterial}`;

    let nextPlant = '';
    let nextMaterial = '';

    if (hasMultipleSelected && selectedRowKeys?.length > 0) {
      const currentIndex = selectedRowKeys.findIndex((key) => key === currentKey);

      if (currentIndex === -1) {
        console.warn('Current key not found in selectedRowKeys');
        return;
      }

      const nextIndex = (currentIndex + direction + selectedRowKeys.length) % selectedRowKeys.length;
      const nextKey = selectedRowKeys[nextIndex];

      // Extract plant and material from the key
      nextPlant = nextKey.slice(0, currentPlant.length);
      nextMaterial = nextKey.slice(currentPlant.length);
    } else {
      if (!navList?.length) {
        console.warn('Nav List is empty or unavailable');
        return;
      }

      const currentIndex = navList.findIndex(
        (matObj) => matObj?.PLANT_FACILITY_SAP_ID === currentPlant
          && matObj?.MATERIAL_TYPE_SAP_ID === currentMaterial,
      );

      if (currentIndex === -1) {
        console.warn('Current material not found in navList');
        return;
      }

      const nextIndex = (currentIndex + direction + navList.length) % navList.length;
      nextPlant = navList[nextIndex]?.PLANT_FACILITY_SAP_ID || '';
      nextMaterial = navList[nextIndex]?.MATERIAL_TYPE_SAP_ID || '';
    }

    if (nextPlant && nextMaterial) {
      const { mode, worklistID } = history?.location?.state || {};
      history.push(`/material-details/${nextPlant}/${nextMaterial}`, {
        state: { mode, worklistID },
      });
    } else {
      console.warn('Invalid next plant/material, navigation aborted');
    }
  };


  const extra = () => {
    let isFinalItem = false;
    let prevIndex;
    let nextIndex;
    let prevTooltipText = 'Go to the previous item';
    let nextTooltipText = 'Go to the next item';

    const hasMultipleSelected = selectedRowKeys?.length > 1;
    const currentKey = `${plant}${material}`;
    if (selectedRowKeys?.length > 0) {
      const lastKey = selectedRowKeys[selectedRowKeys.length - 1];
      isFinalItem = currentKey === lastKey;
    } else if (navList?.length > 0) {
      const lastNavItem = navList[navList.length - 1];
      const lastNavKey = `${lastNavItem?.PLANT_FACILITY_SAP_ID}${lastNavItem?.MATERIAL_TYPE_SAP_ID}`;
      isFinalItem = currentKey === lastNavKey;
    }

    const keyToFind = `${plant}${material}`;

    if (hasMultipleSelected && selectedRowKeys?.length > 0) {
      const currentSelectedIndex = selectedRowKeys.findIndex((key) => key === keyToFind);

      if (currentSelectedIndex !== -1) {
        prevIndex = currentSelectedIndex === 0 ? selectedRowKeys.length - 1 : currentSelectedIndex - 1;
        nextIndex = (currentSelectedIndex + 1) % selectedRowKeys.length;

        prevTooltipText = `Go to item ${prevIndex + 1} of ${selectedRowKeys.length}`;
        nextTooltipText = isFinalItem
          ? `Go to item 1 of ${selectedRowKeys.length}`
          : `Go to item ${nextIndex + 1} of ${selectedRowKeys.length}`;
      }
    } else if (effectiveRowKeys?.length > 0) {
      const curIndex = effectiveRowKeys.findIndex((key) => key === currentMaterial);

      if (curIndex !== -1) {
        prevIndex = curIndex === 0 ? effectiveRowKeys.length - 1 : curIndex - 1;
        nextIndex = (curIndex + 1) % effectiveRowKeys.length;

        prevTooltipText = `Go to item ${prevIndex + 1} of ${effectiveRowKeys.length}`;
        nextTooltipText = isFinalItem
          ? 'Go to first material'
          : `Go to item ${nextIndex + 1} of ${effectiveRowKeys.length}`;
      }
    }


    if (isFinalItem) {
      if (hasMultipleSelected) {
        nextTooltipText = `Go to item 1 of ${selectedRowKeys?.length || 0}`;
      } else {
        nextTooltipText = 'Go to first material';
      }
    }

    return (
      <div className={styles.extra}>
        {navList?.length ? (
          <>
            <Tooltip title={prevTooltipText}>
              <ArrowLeftOutlined onClick={() => goToNewMaterial(-1, true)} />
            </Tooltip>
            <Tooltip title={nextTooltipText}>
              {isFinalItem ? (
                <UndoOutlined onClick={() => goToNewMaterial(+1)} />
              ) : (
                <ArrowRightOutlined onClick={() => goToNewMaterial(+1, true)} />
              )}
            </Tooltip>
          </>
        ) : null}

        <HeaderOptions data={data} style={{ margin: '0px 4px' }} canEdit={canEdit} />

        {canEdit && (
        <>
          <Button
            className="headerButton"
            size="small"
            type="primary"
            onClick={() => setSimulationVisible(true)}
          >
            Simulation
          </Button>

          <Tooltip title="Mark material as reviewed">
            <Button className="headerButton" size="small" type="primary" onClick={review}>
              Review
            </Button>
          </Tooltip>

          <Button
            className="headerButton"
            size="small"
            type="primary"
            onClick={() => setCurrentCalender('postpone')}
          >
            Postpone
          </Button>

          <Button
            className="headerButton"
            size="small"
            type="primary"
            onClick={() => setCurrentCalender('inspect')}
          >
            Inspect
          </Button>
        </>
        )}
      </div>
    );
  };

  // const backIcon = () => {
  //   const title = `Return to ${history.location?.state?.worklistID ? 'Worklist' : 'Material List'}`;
  //   return (
  //     <div>
  //       <Tooltip title={title} placement="bottomLeft">
  //         <Button shape="round">
  //           <ArrowLeftOutlined />
  //         </Button>
  //       </Tooltip>
  //     </div>
  //   );
  // };

  return (
    !dataIsUpdated
      ? <Loading spinning={!notFound} text={notFound ? 'Material not found' : 'Loading'} />
      : (
        <>
          <div className={styles.materialDetails}>
            <div className={styles.leftColumn}>
              <PageHeader
                className="leftColumn"
                // backIcon={backIcon()}
                onBack={() => backToMaterialList()}
                title="Details"
                extra={extra()}
                style={{ margin: '0px', padding: '0px' }}
              >
                <SummaryElements visible={simulationVisible} setVisible={setSimulationVisible} />
              </PageHeader>
            </div>
            <div className={styles.divider} />
            <div className={styles.rightColumn}>
              <Tabs className="matDetsTabs" defaultActiveKey="1" size="small" style={{ height: '100%' }}>
                <TabPane tab="Additional Data" key="1" style={{ height: '100%' }}>
                  <TabElements excludePM10={excludePM10} />
                </TabPane>
                <TabPane tab="Related Plants" key="2" style={{ height: '100%' }}>
                  <PlantElements hasViewerAccess={canEdit} />
                </TabPane>
              </Tabs>
            </div>
          </div>
          <CalendarModal
            visible={calenderVisible}
            onOK={currentCalender.onOK}
            onCancel={currentCalender.onCancel}
            text={currentCalender.text}
          />
        </>
      )
  );
};
export default MaterialDetails;
